import React from 'react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';

// --- Tiptap Core Extensions ---
import { StarterKit } from '@tiptap/starter-kit';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';

// --- UI Primitives ---
import { Spacer } from './tiptap-ui-primitive/spacer';
import {
    Toolbar,
    ToolbarGroup,
    ToolbarSeparator,
} from './tiptap-ui-primitive/toolbar';

// --- Tiptap Node ---
import './tiptap-node/blockquote-node/blockquote-node.css';
import './tiptap-node/code-block-node/code-block-node.css';
import './tiptap-node/list-node/list-node.css';
import './tiptap-node/heading-node/heading-node.css';
import './tiptap-node/paragraph-node/paragraph-node.css';

// --- Tiptap UI ---
import { HeadingDropdownMenu } from './tiptap-ui/heading-dropdown-menu';
import { ListDropdownMenu } from './tiptap-ui/list-dropdown-menu';
import { BlockquoteButton } from './tiptap-ui/blockquote-button';
import { CodeBlockButton } from './tiptap-ui/code-block-button';
import { MarkButton } from './tiptap-ui/mark-button';
import { UndoRedoButton } from './tiptap-ui/undo-redo-button';

// --- Hooks ---
import { useIsBreakpoint } from '../hooks/use-is-breakpoint';

// --- Styles ---
import './tiptap-templates/simple/simple-editor.css';

interface EditorProps {
    content?: string;
    onUpdate?: (content: string) => void;
    placeholder?: string;
    readOnly?: boolean;
}

export const Editor: React.FC<EditorProps> = ({
    content,
    onUpdate,
    placeholder = 'Start typing...',
    readOnly = false
}) => {
    const isMobile = useIsBreakpoint();

    // Track external content updates
    const isUserTyping = React.useRef(false);
    const externalContentRef = React.useRef<string>('');

    const editor = useEditor({
        immediatelyRender: false,
        editable: !readOnly,
        editorProps: {
            attributes: {
                autocomplete: 'off',
                autocorrect: 'off',
                autocapitalize: 'off',
                'aria-label': readOnly ? 'Document viewer' : 'Document editor',
                class: 'simple-editor outline-none py-4 leading-7 text-gray-900 min-h-[500px] prose prose-lg max-w-none focus:outline-none [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:my-8 [&_h1]:text-gray-900 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-gray-900 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:leading-normal [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-gray-900 [&_p]:my-3 [&_p]:leading-7 [&_ul]:my-3 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:my-3 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:my-1 [&_li]:leading-relaxed [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:my-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_pre]:bg-gray-100 [&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:font-mono [&_pre]:text-sm [&_pre]:leading-relaxed [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-sm [&_*]:transition-all [&_*]:duration-75',
            },
        },
        extensions: [
            StarterKit.configure({
                horizontalRule: false,
            }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TaskList,
            TaskItem.configure({ nested: true }),
            Highlight.configure({ multicolor: true }),
        ],
        content: '<p></p>',
        onUpdate: ({ editor }) => {
            if (!readOnly) {
                isUserTyping.current = true;
                const newContent = editor.getHTML();
                externalContentRef.current = newContent;
                onUpdate?.(newContent);
                // Reset the typing flag after a brief delay
                setTimeout(() => {
                    isUserTyping.current = false;
                }, 100);
            }
        },
    });

    React.useEffect(() => {
        // Only update content if it's coming from external source (not user typing)
        if (editor && content && content !== externalContentRef.current && !isUserTyping.current) {
            console.log('Setting content from external source:', content);
            editor.commands.setContent(content);
            externalContentRef.current = content;
        }
    }, [editor, content]);

    if (!editor) {
        return <div className="p-8 text-center text-gray-500">Loading editor...</div>;
    }

    return (
        <div className="w-full bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <EditorContext.Provider value={{ editor }}>
                {!readOnly && (
                    <Toolbar className="w-full bg-gray-50 overflow-x-auto whitespace-nowrap flex-nowrap scrollbar-hide px-1 sm:px-0">
                        <Spacer />

                        <ToolbarGroup>
                            <UndoRedoButton action="undo" />
                            <UndoRedoButton action="redo" />
                        </ToolbarGroup>

                        <ToolbarSeparator />

                        <ToolbarGroup>
                            <HeadingDropdownMenu levels={[1, 2, 3]} portal={isMobile} />
                            <ListDropdownMenu
                                types={['bulletList', 'orderedList']}
                                portal={isMobile}
                            />
                        </ToolbarGroup>

                        <ToolbarSeparator />

                        <ToolbarGroup>
                            <MarkButton type="bold" />
                            <MarkButton type="italic" />
                            <MarkButton type="code" />
                        </ToolbarGroup>

                        <Spacer />
                    </Toolbar>
                )}
                <div className="px-2 md:px-20 lg:px-40 text-gray-500 text-sm">
                    <EditorContent
                        editor={editor}
                        role="presentation"
                        className="py-4 min-h-[500px] mt-[56px] sm:mt-0"
                    />
                </div>
            </EditorContext.Provider>
        </div>
    );
};